/* REVIEW-111: retain the image's compositing layer through eased zoom and reset. */
(() => {
  'use strict';
  function initialize(scope = document) {
    scope.querySelectorAll('.product-media-stack').forEach(stack => {
      const modal = stack.querySelector('#CustomMediaModal');
      if (!modal || modal.dataset.initialized) return;
      const media = JSON.parse(stack.querySelector('[data-bw-gallery-media]').textContent);
      if (!media.length) return;
      modal.dataset.initialized = 'true';
      const image = modal.querySelector('#main-media'), counter = modal.querySelector('#media-index');
      const status = modal.querySelector('[data-media-status]'), root = document.documentElement;
      const cache = new Map();
      let shown = 0, requested = 0, version = 0, gesture, opener, overflow, priority;
      const wrap = index => (index + media.length) % media.length;
      const frame = modal.querySelector('.fullscreen-slider'), pointers = new Map();
      let imageZoom = 1, panX = 0, panY = 0, lastTap = null, pinch = null;
      let zoomMotion = 0, paintedZoom = {zoom:1, x:0, y:0};
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const fillZoom = () => {
        const widthFit = modal.clientWidth / (image.offsetWidth || 1);
        // Wide images fill the width, never the portrait viewport's height.
        return Math.max(1, widthFit, image.offsetWidth >= image.offsetHeight ? 1 : frame.clientHeight / (image.offsetHeight || 1));
      };
      const zoomLimit = () => Math.max(6, fillZoom());
      function renderZoom(state) {
        paintedZoom = state;
        // Keep one image layer even at 100%; avoid dropping it at the end of
        // an animated reset, which can expose stale edge pixels in Safari.
        image.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.zoom})`;
      }
      function stopZoomMotion(adoptPosition = false) {
        if (!zoomMotion) return;
        cancelAnimationFrame(zoomMotion); zoomMotion = 0;
        // Pick up an interrupted enlargement where it is currently visible.
        // A reset has already enabled swiping, so finish at 100% instead.
        if (adoptPosition && imageZoom > 1.01) {
          imageZoom = paintedZoom.zoom; panX = paintedZoom.x; panY = paintedZoom.y;
        }
      }
      function paintZoom(smooth = false) {
        stopZoomMotion();
        const maxX = Math.max(0, (image.offsetWidth * imageZoom - modal.clientWidth) / 2);
        const maxY = Math.max(0, (image.offsetHeight * imageZoom - frame.clientHeight) / 2);
        panX = Math.max(-maxX, Math.min(maxX, panX));
        panY = Math.max(-maxY, Math.min(maxY, panY));
        image.dataset.zoom = String(imageZoom);
        const target = {zoom:imageZoom, x:panX, y:panY};
        if (!smooth || reducedMotion.matches || !modal.open) { renderZoom(target); return; }
        const from = paintedZoom, started = performance.now();
        function step(now) {
          const progress = Math.min(1, Math.max(0, (now - started) / 280));
          // Smoothstep: start and finish gently, without a fast initial jump.
          const eased = progress * progress * (3 - 2 * progress);
          renderZoom(progress === 1 ? target : {
            zoom:from.zoom + (target.zoom - from.zoom) * eased,
            x:from.x + (target.x - from.x) * eased,
            y:from.y + (target.y - from.y) * eased
          });
          zoomMotion = progress < 1 ? requestAnimationFrame(step) : 0;
        }
        zoomMotion = requestAnimationFrame(step);
      }
      function resetImageZoom(smooth = false) { imageZoom = 1; panX = panY = 0; lastTap = null; paintZoom(smooth); }
      function toggleImageZoom(x, y) {
        if (imageZoom > 1.01) resetImageZoom(true);
        else {
          const bounds = image.getBoundingClientRect();
          imageZoom = fillZoom();
          panX = (bounds.left + bounds.width / 2 - x) * (imageZoom - 1);
          panY = (bounds.top + bounds.height / 2 - y) * (imageZoom - 1);
          lastTap = null; paintZoom(true);
        }
        status.textContent = imageZoom > 1.01 ? 'Image enlarged to fill the screen' : 'Image restored to original size';
      }
      function resetGesture() {
        const ids = [...pointers.keys()]; pointers.clear(); gesture = null; pinch = null;
        image.removeAttribute('data-dragging');
        ids.forEach(id => { if (modal.hasPointerCapture(id)) modal.releasePointerCapture(id); });
      }
      function beginPinch() {
        const [a,b] = [...pointers.values()], bounds = frame.getBoundingClientRect();
        const centerX = bounds.left + bounds.width / 2, centerY = bounds.top + bounds.height / 2;
        pinch = {distance:Math.max(1,Math.hypot(a.x-b.x,a.y-b.y)), zoom:imageZoom, centerX, centerY,
          anchorX:((a.x+b.x)/2-centerX-panX)/imageZoom,
          anchorY:((a.y+b.y)/2-centerY-panY)/imageZoom};
        gesture = null; lastTap = null;
      }
      function prepare(index) {
        if (!cache.has(index)) {
          const pending = new Promise((resolve, reject) => {
            const next = new Image();
            const timer = setTimeout(() => { next.onload = next.onerror = null; reject(Error('Image timeout')); }, 15000);
            next.onerror = () => { clearTimeout(timer); reject(Error('Image unavailable')); };
            next.onload = async () => {
              try { if (next.decode) await next.decode(); clearTimeout(timer); resolve(next); }
              catch (error) { clearTimeout(timer); reject(error); }
            };
            next.src = media[index].src;
          });
          cache.set(index, pending);
          pending.catch(() => { if (cache.get(index) === pending) cache.delete(index); });
        }
        return cache.get(index);
      }
      function prefetch(index) {
        const nearby = new Set([index, wrap(index - 1), wrap(index + 1)]);
        for (const key of cache.keys()) if (!nearby.has(key)) cache.delete(key);
        nearby.forEach(i => { if (i !== index) prepare(i).catch(() => {}); });
      }
      async function show(index) {
        requested = wrap(index); const target = requested, ticket = ++version;
        modal.setAttribute('aria-busy', 'true'); status.textContent = '';
        try {
          const next = await prepare(target);
          if (ticket !== version || !modal.open) return;
          if (shown !== target) { resetGesture(); resetImageZoom(); }
          image.src = next.src; image.alt = media[target].alt;
          shown = target; counter.textContent = `${shown + 1}/${media.length}`;
          prefetch(shown);
        } catch (_) {
          if (ticket !== version || !modal.open) return;
          requested = shown;
          status.textContent = 'Image could not be loaded. Please try again.';
        } finally {
          if (ticket === version) modal.removeAttribute('aria-busy');
        }
      }
      function open(trigger, index, keyboard) {
        if (modal.open) return;
        opener = trigger; overflow = root.style.getPropertyValue('overflow'); priority = root.style.getPropertyPriority('overflow');
        // The already visible thumbnail remains while its full image is prepared.
        shown = requested = index;
        const thumbnail = trigger.querySelector('img') || stack.querySelector('[data-bw-gallery-id] img');
        image.src = thumbnail?.currentSrc || media[index].src;
        image.alt = media[index].alt; counter.textContent = `${index + 1}/${media.length}`;
        resetImageZoom();
        modal.dataset.inputMode = keyboard ? 'keyboard' : 'pointer';
        modal.showModal(); root.style.setProperty('overflow', 'hidden');
        root.classList.add('bw-image-viewer-open'); show(index);
      }
      // Capture before nested Dawn modal buttons; legacy Magnific ignores marked image links.
      stack.addEventListener('click', event => {
        const trigger = event.target.closest('[data-bw-gallery-id], [data-see-all-open], [data-media-popup-trigger]');
        if (!trigger || !stack.contains(trigger) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        const id = trigger.dataset.bwGalleryId;
        const index = id ? media.findIndex(item => String(item.id) === id) : 0;
        if (index < 0) return;
        event.preventDefault(); event.stopPropagation(); open(trigger, index, event.detail === 0);
      }, true);
      modal.querySelectorAll('[data-media-direction]').forEach(button => {
        button.disabled = media.length < 2;
        button.addEventListener('click', () => show(requested + Number(button.dataset.mediaDirection)));
      });
      modal.querySelector('[data-media-close]').addEventListener('click', () => modal.close());
      modal.addEventListener('close', () => {
        ++version; resetGesture(); resetImageZoom(); requested = shown; modal.removeAttribute('aria-busy');
        if (overflow) root.style.setProperty('overflow', overflow, priority); else root.style.removeProperty('overflow');
        root.classList.remove('bw-image-viewer-open');
        opener?.isConnected && opener.focus({preventScroll:true});
      });
      modal.addEventListener('keydown', event => {
        modal.dataset.inputMode = 'keyboard';
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault(); show(requested + (event.key === 'ArrowRight' ? 1 : -1));
      });
      // Own image gestures only inside this dialog. Browser/page zoom elsewhere is untouched.
      // Native viewport pinch cannot be reset programmatically; custom image pinch can.
      modal.style.touchAction = 'none';
      let viewportWidth = innerWidth;
      window.visualViewport?.addEventListener('resize', () => {
        if (viewportWidth !== innerWidth) { resetGesture(); resetImageZoom(); }
        viewportWidth = innerWidth; paintZoom();
      });
      modal.addEventListener('pointerdown', event => {
        modal.dataset.inputMode = 'pointer';
        if (!modal.open || event.button !== 0 || event.target.closest('button, a, input, select, textarea')) return;
        stopZoomMotion(true); paintZoom();
        pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});
        modal.setPointerCapture(event.pointerId); image.setAttribute('data-dragging','');
        if (pointers.size === 2) { beginPinch(); return; }
        if (pointers.size > 2) { gesture = null; pinch = null; lastTap = null; return; }
        gesture = {id:event.pointerId,x:event.clientX,y:event.clientY,width:innerWidth,
          time:performance.now(),movement:0,image:event.target === image,
          pointerType:event.pointerType,panX,panY};
      });
      modal.addEventListener('pointermove', event => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});
        if (pointers.size === 2 && pinch) {
          const [a,b] = [...pointers.values()];
          imageZoom = Math.max(1,Math.min(zoomLimit(),pinch.zoom*Math.hypot(a.x-b.x,a.y-b.y)/pinch.distance));
          panX = (a.x+b.x)/2 - pinch.centerX - pinch.anchorX*imageZoom;
          panY = (a.y+b.y)/2 - pinch.centerY - pinch.anchorY*imageZoom;
          paintZoom(); return;
        }
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX-gesture.x, dy = event.clientY-gesture.y;
        gesture.movement = Math.max(gesture.movement,Math.hypot(dx,dy));
        if (imageZoom > 1.01) {
          panX = gesture.panX+dx; panY = gesture.panY+dy; paintZoom();
        } else if (Math.abs(dy)>18 && Math.abs(dy)>Math.abs(dx)*1.15) {
          gesture = null; lastTap = null;
        }
      });
      modal.addEventListener('pointerup', event => {
        if (!pointers.has(event.pointerId)) return;
        const start = gesture, wasPinching = !!pinch || pointers.size>1;
        pointers.delete(event.pointerId); gesture = null; pinch = null;
        if (modal.hasPointerCapture(event.pointerId)) modal.releasePointerCapture(event.pointerId);
        image.removeAttribute('data-dragging');
        if (wasPinching) {
          lastTap = null;
          if (pointers.size === 1) {
            const [id,p] = [...pointers.entries()][0];
            gesture = {id,x:p.x,y:p.y,panX,panY,width:innerWidth,time:performance.now(),movement:0,panOnly:true};
          }
          if (imageZoom < 1.01) resetImageZoom();
          return;
        }
        if (!start || start.id!==event.pointerId || start.width!==innerWidth || start.panOnly) return;
        const dx=event.clientX-start.x, dy=event.clientY-start.y, now=performance.now();
        const tap=start.image && start.pointerType!=='mouse' &&
          Math.max(start.movement,Math.hypot(dx,dy))<=10 && now-start.time<=300;
        if (tap) {
          if (lastTap && now-lastTap.time<=350 && lastTap.index===shown &&
              Math.hypot(event.clientX-lastTap.x,event.clientY-lastTap.y)<=32) {
            event.preventDefault(); toggleImageZoom(event.clientX,event.clientY);
          } else lastTap={time:now,x:event.clientX,y:event.clientY,index:shown};
          return;
        }
        lastTap=null;
        if (imageZoom>1.01 || media.length<2) return;
        const threshold=Math.max(18,Math.min(28,innerWidth*0.05));
        if (Math.abs(dx)>=threshold && Math.abs(dx)>Math.abs(dy)*1.15) show(requested+(dx<0?1:-1));
      });
      modal.addEventListener('pointercancel', () => { resetGesture(); lastTap=null; });
      modal.addEventListener('lostpointercapture', event => {
        if (pointers.has(event.pointerId)) { resetGesture(); lastTap=null; }
      });
      window.addEventListener('resize', () => paintZoom());
      image.addEventListener('load', () => paintZoom());
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once:true});
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
